import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Flex,
  Button,
  IconButton,
  Text,
  Badge,
  HStack,
  useBreakpointValue,
  Menu,
  Portal,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { FiMenu, FiHelpCircle, FiShield, FiMoreVertical, FiLogOut, FiCreditCard, FiFileText, FiMessageSquare, FiMail, FiCompass } from 'react-icons/fi'
import { useNavigate, useLocation } from 'react-router-dom'
import { coachNavTarget } from '../../utils/coachNavigation'
import { UserDropdown } from './UserDropdown'
import { ProfileSwitcher } from './ProfileSwitcher'
import { Logo } from '../Brand/Logo'
import { NotificationsDropdown } from './NotificationsDropdown'
import { useAuth } from '../../contexts/AuthContext'
import { useActiveProfile } from '../../contexts/ActiveProfileContext'
import { Tooltip } from '../ui/Tooltip'
import { Icon } from '../ui/Icon'
import { signOut } from 'firebase/auth'
import { auth } from '../../config/firebase'
import { getAdminStatusFromUser } from '../../utils/jwtDecoder'
import { useHasCollaborators, useCollaboratorConversations } from '../../hooks/useCollaboratorMessaging'

interface DashboardNavbarProps {
  onMenuClick: () => void
  showMenuButton?: boolean
  topOffset?: string
}

export const DashboardNavbar: React.FC<DashboardNavbarProps> = ({
  onMenuClick,
  showMenuButton = false,
  topOffset = '0px',
}) => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { activeProfile } = useActiveProfile()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useBreakpointValue({ base: true, md: false })
  
  // Check if user has any collaborators (for showing messages button)
  const hasCollaborators = useHasCollaborators(activeProfile?.profileId)

  // Get unread messages count
  const { conversations: collaboratorConversations } = useCollaboratorConversations(activeProfile?.profileId)
  const totalUnreadCount = useMemo(() => {
    return collaboratorConversations.reduce((total, conv) => total + (conv.unreadCount || 0), 0)
  }, [collaboratorConversations])

  // Check if user has admin privileges
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false)
        return
      }

      // Use utility function to avoid network requests when checking admin status
      const adminStatus = await getAdminStatusFromUser(currentUser)
      setIsAdmin(adminStatus)
    }

    checkAdminStatus()
  }, [currentUser])

  const handleLogout = async () => {
    try {
      await signOut(auth)
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <Box
      as="header"
      position="fixed"
      top={topOffset}
      left={0}
      right={0}
      zIndex="banner"
      bg="bg"
      borderBottom="1px"
      borderColor="border.muted"
      px={{ base: 4, md: 6 }}
      py={3}
      shadow="sm"
      role="banner"
      aria-label={t('dashboard.navbar.main_navigation')}
      data-header="main"
    >
      <Flex h="10" alignItems="center" justifyContent="space-between">
        {/* Left side - Logo, Menu Button, and Current Profile */}
        <Flex alignItems="center" gap={3}>
          {showMenuButton && (
            <Tooltip label={t('dashboard.navbar.open_sidebar_menu')} placement="bottom">
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('dashboard.navbar.open_sidebar_menu')}
                onClick={onMenuClick}
                _hover={{ bg: 'wellness.mint' }}
              >
                <FiMenu />
              </Button>
            </Tooltip>
          )}

          <Logo size="lg" display={{ base: 'none', md: 'block' }} />

          {/* Admin Badge - Show when user has admin privileges */}
          {isAdmin && (
            <Badge colorScheme="orange" variant="subtle" size="sm">
              <HStack gap={1}>
                <FiShield size="12px" />
                <Text>Admin</Text>
              </HStack>
            </Badge>
          )}

          {/* Profile Switcher — compact on mobile so the profile name/username
              block doesn't force Notifications and the overflow menu off-screen */}
          <Box data-tour="profile-selector">
            <ProfileSwitcher variant={isMobile ? 'compact' : 'full'} />
          </Box>
        </Flex>

        {/* Right side - other nav buttons */}
        <Flex alignItems="center" gap={{ base: 2, md: 3 }}>
          {/* Notifications - Visible on both mobile and desktop */}
          <NotificationsDropdown />

          {/* Desktop Navigation Items - Hidden on mobile */}
          <Flex display={{ base: 'none', md: 'flex' }} alignItems="center" gap={3}>
            {/* Discoveries — also in the sidebar now; kept here for quick access.
                TODO(discovery-badge): badge with newResultsCount once a shared
                token fetch exists app-wide (today only ProfilePage fetches it,
                and duplicating that call into every navbar render would add
                an extra request per page load — needs its own hook first). */}
            <Tooltip label="Discoveries" placement="bottom">
              <Button
                size="sm"
                variant="ghost"
                aria-label="Discoveries"
                onClick={() => navigate('/discoveries')}
                _hover={{ bg: 'wellness.mint' }}
              >
                <FiCompass />
              </Button>
            </Tooltip>

            {/* GeneChat */}
            <Tooltip label={t('dashboard.navbar.genechat')} placement="bottom">
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('dashboard.navbar.open_genechat')}
                onClick={() => navigate(coachNavTarget(location.pathname))}
                _hover={{ bg: 'wellness.mint' }}
              >
                <Icon name="sparkling-fill" size="sm" color="brand.600" />
              </Button>
            </Tooltip>

            {/* Messages - Show for all users */}
            <Tooltip label={t('dashboard.navbar.messages', 'Messages')} placement="bottom">
              <Box position="relative">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('dashboard.navbar.messages', 'Messages')}
                  onClick={() => navigate('/messages')}
                  _hover={{ bg: 'wellness.mint' }}
                >
                  <FiMail />
                </Button>
                {totalUnreadCount > 0 && (
                  <Badge
                    colorScheme="red"
                    position="absolute"
                    top="-2px"
                    right="-2px"
                    borderRadius="full"
                    fontSize="0.6em"
                    px={1}
                    minW="16px"
                    textAlign="center"
                    pointerEvents="none"
                  >
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </Badge>
                )}
              </Box>
            </Tooltip>

            {/* Support */}
            <Tooltip label={t('dashboard.navbar.support')} placement="bottom">
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('dashboard.navbar.support')}
                onClick={() => navigate('/support')}
                _hover={{ bg: 'wellness.mint' }}
              >
                <FiHelpCircle />
              </Button>
            </Tooltip>
          </Flex>

          {/* Mobile Navigation Menu - Visible only on mobile */}
          <Box display={{ base: 'block', md: 'none' }}>
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton
                  aria-label="More options"
                  variant="ghost"
                  size="sm"
                  _hover={{ bg: 'wellness.mint' }}
                >
                  <FiMoreVertical />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content zIndex={1400}>
                    <Menu.Item value="discoveries" onClick={() => navigate('/discoveries')}>
                      <HStack gap={2}>
                        <FiCompass />
                        <Text>My Discoveries</Text>
                      </HStack>
                    </Menu.Item>
                    <Menu.Item value="genechat" onClick={() => navigate(coachNavTarget(location.pathname))}>
                      <HStack gap={2}>
                        <Icon name="sparkling-fill" size="sm" color="brand.600" />
                        <Text>{t('dashboard.navbar.genechat')}</Text>
                      </HStack>
                    </Menu.Item>
                    <Menu.Item value="messages" onClick={() => navigate('/messages')}>
                      <HStack gap={2}>
                        <FiMail />
                        <Text>{t('dashboard.navbar.messages', 'Messages')}</Text>
                        {totalUnreadCount > 0 && (
                          <Badge colorScheme="red" borderRadius="full" fontSize="xs">
                            {totalUnreadCount}
                          </Badge>
                        )}
                      </HStack>
                    </Menu.Item>
                    <Menu.Item value="support" onClick={() => navigate('/support')}>
                      <HStack gap={2}>
                        <FiHelpCircle />
                        <Text>{t('dashboard.navbar.support')}</Text>
                      </HStack>
                    </Menu.Item>
                    
                    <Menu.Separator />
                    
                    <Menu.Item value="plans" onClick={() => navigate('/plans-and-pricing')}>
                      <HStack gap={2}>
                        <FiCreditCard />
                        <Text>{t('dashboard.user_dropdown.menu_items.plans_pricing')}</Text>
                      </HStack>
                    </Menu.Item>
                    <Menu.Item value="terms" onClick={() => navigate('/terms')}>
                      <HStack gap={2}>
                        <FiFileText />
                        <Text>{t('dashboard.user_dropdown.menu_items.terms_of_service')}</Text>
                      </HStack>
                    </Menu.Item>
                    <Menu.Item value="privacy" onClick={() => navigate('/privacy')}>
                      <HStack gap={2}>
                        <FiFileText />
                        <Text>{t('dashboard.user_dropdown.menu_items.privacy_policy')}</Text>
                      </HStack>
                    </Menu.Item>
                    <Menu.Separator />
                    <Menu.Item value="logout" onClick={handleLogout} color="red.500">
                      <HStack gap={2}>
                        <FiLogOut color="var(--chakra-colors-red-500)" />
                        <Text color="red.500">{t('dashboard.user_dropdown.menu_items.logout')}</Text>
                      </HStack>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>

          {/* Account menu stays in the top right on every width. On a phone the
              name is hidden and only the initial shows, so Settings stays one tap away. */}
          <UserDropdown />
        </Flex>
      </Flex>
    </Box>
  )
}
